import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  // Empty, not prefilled. This form shipped with the admin address and its password
  // already typed in, on the one screen anybody who reaches the app can see.
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user && user !== false) return <Navigate to={user.role === "Employee" ? "/me" : "/"} />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await login(email, password);
      // Not "welcome back" for an account carrying a password somebody else issued: it is
      // being handed straight to the screen that asks it to choose one, and a greeting over
      // that reads as though signing in is finished when it is not.
      if (!u.must_change_password) toast.success("Welcome back");
      nav(u.role === "Employee" ? "/me" : "/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      <div className="hidden md:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div>
          <img src="/brand/shakun-logo-light.svg" alt="Shakun Polymers"
               className="h-14 w-auto" width="235" height="56" />
        </div>
        <div className="max-w-md relative z-10">
          <div className="overline text-white/60">Shakun Polymers Pvt. Ltd.</div>
          <h1 className="font-heading text-4xl lg:text-5xl font-bold leading-tight mt-3">Training. Attendance. Certificates. In one place.</h1>
          <p className="text-white/70 mt-4 text-sm">A central system for HR to manage training requests, attendance and participation certificates across all units.</p>
        </div>
        <div className="text-xs text-white/70">© {new Date().getFullYear()} Shakun Polymers · Internal use only</div>
        <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-white/5 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-8">
        <Card className="w-full max-w-md p-8 border border-border" data-testid="login-card">
          <div className="overline text-primary mb-2">Welcome back</div>
          <h2 className="font-heading font-bold text-2xl">Sign in to your account</h2>
          <p className="text-sm text-muted-foreground mt-1">Use your Shakun HR credentials</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <Label htmlFor="password">Password</Label>
                {/* Somebody who cannot get in will otherwise keep trying until the
                    throttle locks their address out for the window. */}
                <Link to="/forgot-password" className="text-xs text-primary hover:underline"
                      data-testid="login-forgot-password">Forgotten it?</Link>
              </div>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
            </div>
            {error && <div className="text-sm text-destructive-text" data-testid="login-error">{error}</div>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
